import { useTranslation } from 'react-i18next';
import { t as translate } from 'i18n';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  MenuItem,
  Paper,
  Tab,
  Table,
  ToggleButton,
  ToggleButtonGroup,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useTenantSettings, useUpdateTenantSettings, useMembers, useUpdateMemberRole, useRemoveMember } from 'api/settings';
import { useInvitations, useCreateInvitation, useRevokeInvitation } from 'api/notifications';
import { extractApiError } from 'api/errors';
import useAuthStore, { selectFeature } from 'stores/authStore';
import useThemeStore, { useChartTheme, useMode } from 'stores/themeStore';
import { tokens } from 'theme';
import { CHART_THEMES } from 'theme/chartThemes';
import useRole from 'hooks/useRole';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';
import BillingTab from './BillingTab';
import AccountSettings from './AccountSettings';
import { formatDate } from 'i18n/format';
import LanguageSwitcher from 'i18n/LanguageSwitcher';

const ROLE_COLORS = { owner: 'error', admin: 'warning', member: 'default' };
const roleOptions = () => [
  { value: 'owner', label: translate('shell.owner') },
  { value: 'admin', label: translate('shell.admin') },
  { value: 'member', label: translate('shell.member') },
];

function initials(name, email) {
  if (name) return name.split(' ').map((p) => p[0]).join('').toUpperCase().slice(0, 2);
  return (email || '?')[0].toUpperCase();
}

// Tab state lives in ?tab= so the PSP checkout return URL can deep-link
// straight into Billing (…/console/settings?tab=billing&checkout=1).
const TAB_KEYS = ['organization', 'team', 'billing', 'appearance'];
const accountTabs = () => [
  { key: 'profile', label: translate('shell.profile') },
  { key: 'security', label: translate('shell.security') },
  { key: 'email', label: translate('shell.email') },
  { key: 'sessions', label: translate('shell.sessions') },
];

export default function SettingsPage() {
  const { t: translate } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const accountEnabled = useAuthStore(selectFeature('account_console'));
  const tabKeys = accountEnabled ? [...TAB_KEYS, ...accountTabs().map((tab) => tab.key)] : TAB_KEYS;
  const tabIndex = Math.max(tabKeys.indexOf(searchParams.get('tab') || 'organization'), 0);

  const selectTab = (index) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tabKeys[index]);
    setSearchParams(next, { replace: true });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <SettingsIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>{translate('shell.settings')}</Typography>
      </Box>
      <Tabs value={tabIndex} onChange={(_, v) => selectTab(v)} variant="scrollable" scrollButtons="auto" sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label={translate('shell.organization')} icon={<BusinessIcon fontSize="small" />} iconPosition="start" />
        <Tab label={translate('shell.team')} icon={<PeopleIcon fontSize="small" />} iconPosition="start" />
        <Tab label={translate('shell.billing')} icon={<CreditCardIcon fontSize="small" />} iconPosition="start" />
        <Tab label={translate('shell.appearance')} icon={<PaletteOutlinedIcon fontSize="small" />} iconPosition="start" />
        {accountEnabled && accountTabs().map((tab) => <Tab key={tab.key} label={tab.label} />)}
      </Tabs>
      {tabIndex === 0 && <TenantTab />}
      {tabIndex === 1 && <TeamTab />}
      {tabIndex === 2 && <BillingTab />}
      {tabIndex === 3 && <AppearanceTab />}
      {accountEnabled && tabIndex >= TAB_KEYS.length && <AccountSettings key={tabKeys[tabIndex]} section={tabKeys[tabIndex]} />}
    </Box>
  );
}

// ── Appearance tab ────────────────────────────────────────────────────────────
// Not a tenant setting: the choice is per viewer, in that browser's
// localStorage — which is why the card says so.
const CHART_THEME_NAMES = { calm: 'shell.chartThemeCalm', evolve: 'shell.chartThemeEvolve' };

function AppearanceTab() {
  const { t: translate } = useTranslation();
  const preference = useThemeStore((s) => s.preference);
  const setPreference = useThemeStore((s) => s.setPreference);
  const chartTheme = useChartTheme();
  const setChartTheme = useThemeStore((s) => s.setChartTheme);
  const mode = useMode();

  return (
    <Box sx={{ maxWidth: 480 }}>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{translate('shell.theme')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{translate('shell.browserTheme')}</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={preference}
          onChange={(_e, next) => { if (next) setPreference(next); }}
          aria-label={translate('shell.theme')}
        >
          <ToggleButton value="light" sx={{ gap: 0.75, px: 2 }}>
            <LightModeOutlinedIcon fontSize="small" />{translate('shell.light')}</ToggleButton>
          <ToggleButton value="dark" sx={{ gap: 0.75, px: 2 }}>
            <DarkModeOutlinedIcon fontSize="small" />{translate('shell.dark')}</ToggleButton>
          <ToggleButton value="system" sx={{ gap: 0.75, px: 2 }}>
            <SettingsBrightnessIcon fontSize="small" />{translate('shell.system')}</ToggleButton>
        </ToggleButtonGroup>
        <Divider sx={{ my: 3 }} />
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>{translate('shell.chartTheme')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{translate('shell.chartThemeHint')}</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={chartTheme}
          onChange={(_e, next) => { if (next) setChartTheme(next); }}
          aria-label={translate('shell.chartTheme')}
        >
          {CHART_THEMES.map((id) => (
            <ToggleButton key={id} value={id} sx={{ gap: 1, px: 2 }}>
              <Box component="span" aria-hidden sx={{ display: 'inline-flex', gap: '3px' }}>
                {tokens(mode, id).chart.series.slice(0, 4).map((colour) => (
                  <Box key={colour} component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: colour }} />
                ))}
              </Box>
              {translate(CHART_THEME_NAMES[id])}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Divider sx={{ my: 3 }} />
        <LanguageSwitcher />
      </Paper>
    </Box>
  );
}

// ── Tenant tab ────────────────────────────────────────────────────────────────
function TenantTab() {
  const { t: translate } = useTranslation();
  const { data: tenant, isLoading } = useTenantSettings();
  const updateTenant = useUpdateTenantSettings();
  const [isEditing, setIsEditing] = useState(false);
  const [name, setName] = useState('');
  const [saveError, setSaveError] = useState(null);

  const startEdit = () => { setName(tenant.name); setIsEditing(true); setSaveError(null); };

  const handleSave = async () => {
    setSaveError(null);
    try {
      await updateTenant.mutateAsync({ name: name.trim() });
      setIsEditing(false);
    } catch (err) {
      setSaveError(extractApiError(err, translate('shell.saveOrganizationFailed')));
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Box sx={{ maxWidth: 480 }}>
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={600}>{translate('shell.organizationInfo')}</Typography>
          {!isEditing ? (
            <Tooltip title={translate('shell.editName')}><IconButton size="small" onClick={startEdit}><EditIcon fontSize="small" /></IconButton></Tooltip>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="contained" startIcon={updateTenant.isPending ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={updateTenant.isPending}>{updateTenant.isPending ? translate('shell.savingAccount') : translate('shell.save')}</Button>
              <Button size="small" startIcon={<CancelIcon />} onClick={() => setIsEditing(false)}>{translate('shell.cancel')}</Button>
            </Box>
          )}
        </Box>

        {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isEditing ? (
            <TextField label={translate('shell.organizationName')} value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
          ) : (
            <Row label={translate('shell.name')} value={tenant?.name} />
          )}
          <Row label={translate('shell.slug')} value={tenant?.slug} mono />
          <Row label={translate('shell.plan')} value={<Chip label={tenant?.plan} size="small" sx={{ textTransform: 'capitalize' }} />} />
          <Row label={translate('shell.publicId')} value={tenant?.public_id} mono small />
        </Box>
      </Paper>
    </Box>
  );
}

function Row({ label, value, mono, small }) {
  useTranslation();
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontFamily={mono ? 'monospace' : undefined} fontSize={small ? 11 : undefined}>{value}</Typography>
    </Box>
  );
}

// ── Team tab ──────────────────────────────────────────────────────────────────
function InviteForm({ onDone }) {
  const { t: translate } = useTranslation();
  const createInvitation = useCreateInvitation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [error, setError] = useState(null);
  // { email, emailSent } — the API creates the invite even when delivery
  // fails, and reports that, so never claim "sent" on its word alone.
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      const invitation = await createInvitation.mutateAsync({ email: email.trim(), role });
      setSuccess({ email: invitation.email, emailSent: invitation.email_sent !== false });
      setEmail('');
      if (onDone) onDone();
    } catch (err) {
      setError(extractApiError(err, translate('shell.sendInvitationFailed')));
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
      <TextField
        label={translate('shell.emailAddress')}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        size="small"
        sx={{ flex: '1 1 220px' }}
      />
      <TextField
        select
        label={translate('shell.role')}
        value={role}
        onChange={(e) => setRole(e.target.value)}
        size="small"
        sx={{ width: 130 }}
      >
        {roleOptions().map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
      </TextField>
      <Button
        type="submit"
        variant="contained"
        size="small"
        startIcon={createInvitation.isPending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
        disabled={createInvitation.isPending || !email}
      >{translate('shell.invite')}</Button>
      {error && <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert>}
      {success && success.emailSent && (
        <Alert severity="success" sx={{ width: '100%' }}>
          {translate('shell.invitationSent', { email: success.email })}
        </Alert>
      )}
      {success && !success.emailSent && (
        <Alert severity="warning" sx={{ width: '100%' }}>
          {translate('shell.invitationNotSent', { email: success.email })}
        </Alert>
      )}
    </Box>
  );
}

function PendingInvitations() {
  const { t: translate } = useTranslation();
  const { isOwner } = useRole();
  const { data: invitations = [], isLoading, isError, refetch } = useInvitations();
  const revokeInvitation = useRevokeInvitation();
  const [now, setNow] = useState(Date.now);
  const [toRevoke, setToRevoke] = useState(null);
  const [revokeError, setRevokeError] = useState(null);
  const pending = invitations.filter((inv) => !inv.is_accepted && !inv.is_revoked && Date.parse(inv.expires_at) > now);

  useEffect(() => {
    // Hide expired rows at their boundary even if the next request is slow or
    // fails. Polling also refreshes changes made from another browser.
    const expiry = Math.min(...invitations.map((inv) => Date.parse(inv.expires_at)).filter((value) => value > now));
    if (!Number.isFinite(expiry)) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), Math.min(Math.max(0, expiry - Date.now()), 2_147_483_647));
    return () => clearTimeout(timer);
  }, [invitations, now]);

  const confirmRevoke = async () => {
    if (!toRevoke || revokeInvitation.isPending || (!isOwner && toRevoke.role === 'owner')) return;
    setRevokeError(null);
    try {
      await revokeInvitation.mutateAsync(toRevoke.id);
      setToRevoke(null);
    } catch (err) {
      setRevokeError(extractApiError(err, translate('shell.revokeInvitationFailed')));
    }
  };

  if (isLoading) return <CircularProgress size={20} sx={{ mt: 3 }} aria-label={translate('shell.pendingInvitations')} />;
  if (pending.length === 0 && !toRevoke && !isError) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AccessTimeIcon fontSize="small" color="action" />{translate('shell.pendingInvitations')}</Typography>
      {isError && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" onClick={() => refetch()}>{translate('shell.retryInvitations')}</Button>}>{translate('shell.loadInvitationsFailed')}</Alert>}
      {pending.length > 0 && (
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>{translate('shell.email')}</TableCell>
              <TableCell>{translate('shell.role')}</TableCell>
              <TableCell>{translate('shell.invitedBy')}</TableCell>
              <TableCell>{translate('shell.expires')}</TableCell>
              <TableCell align="right">{translate('shell.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pending.map((inv) => (
              <TableRow key={inv.id}>
                <TableCell><Typography variant="body2">{inv.email}</Typography></TableCell>
                <TableCell><Chip label={translate(`shell.${inv.role}`, { defaultValue: inv.role })} size="small" color={ROLE_COLORS[inv.role] || 'default'} sx={{ textTransform: 'capitalize' }} /></TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{inv.invited_by_name || '—'}</Typography></TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{formatDate(inv.expires_at)}</Typography></TableCell>
                <TableCell align="right">{(isOwner || inv.role !== 'owner') && <Button size="small" color="error" disabled={revokeInvitation.isPending} onClick={() => { setRevokeError(null); setToRevoke(inv); }}>{translate('shell.revokeInvitation')}</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
      <ConfirmActionDialog
        open={!!toRevoke}
        title={translate('shell.revokeInvitation')}
        confirmWord={translate('shell.revokeInvitationConfirmWord')}
        confirmLabel={translate('shell.revokeInvitation')}
        loading={revokeInvitation.isPending}
        error={revokeError}
        onConfirm={confirmRevoke}
        onClose={() => setToRevoke(null)}
      >
        <Typography variant="body2">{translate('shell.revokeInvitationBody', { email: toRevoke?.email })}</Typography>
      </ConfirmActionDialog>
    </Box>
  );
}

function TeamTab() {
  const { t: translate } = useTranslation();
  const { data: members, isLoading } = useMembers();
  const updateRole = useUpdateMemberRole();
  const removeMember = useRemoveMember();
  const currentUser = useAuthStore((s) => s.user);
  const { isOwner, isAdmin } = useRole();

  const [editingId, setEditingId] = useState(null);
  const [pendingRole, setPendingRole] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [roleError, setRoleError] = useState(null);
  const [toRemove, setToRemove] = useState(null);
  const [removeError, setRemoveError] = useState(null);

  const startRoleEdit = (member) => { setEditingId(member.id); setPendingRole(member.role); setRoleError(null); };

  const handleSaveRole = async (member) => {
    setRoleError(null);
    try {
      await updateRole.mutateAsync({ id: member.id, role: pendingRole });
      setEditingId(null);
    } catch (err) {
      setRoleError(extractApiError(err, translate('shell.updateRoleFailed')));
    }
  };

  const confirmRemove = async () => {
    if (!toRemove) return;
    setRemovingId(toRemove.id);
    setRemoveError(null);
    try {
      await removeMember.mutateAsync(toRemove.id);
      setToRemove(null);
    } catch (err) {
      setRemoveError(extractApiError(err, translate('shell.removeMemberFailed')));
    } finally {
      setRemovingId(null);
    }
  };

  if (isLoading) return <CircularProgress />;

  return (
    <Box>
      {isOwner && (
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
            <PersonAddIcon fontSize="small" color="primary" />
            <Typography variant="subtitle2" fontWeight={600}>{translate('shell.inviteMember')}</Typography>
          </Box>
          <InviteForm />
        </Paper>
      )}
      {roleError && <Alert severity="error" sx={{ mb: 2 }}>{roleError}</Alert>}
      {!isOwner && (
        <Alert severity="info" sx={{ mb: 2 }}>{translate('shell.ownerOnly')}</Alert>
      )}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
        <Table>
          <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
            <TableCell>{translate('shell.member')}</TableCell>
            <TableCell>{translate('shell.email')}</TableCell>
            <TableCell>{translate('shell.role')}</TableCell>
            <TableCell>{translate('shell.status')}</TableCell>
            <TableCell>{translate('shell.joined')}</TableCell>
            {isOwner && <TableCell align="right">{translate('shell.actions')}</TableCell>}
          </TableRow></TableHead>
          <TableBody>
            {members?.map((member) => {
              const isSelf = member.user_id === currentUser?.id;
              return (
                <TableRow key={member.id} sx={{ opacity: member.is_active ? 1 : 0.5 }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>{initials(member.name, member.email)}</Avatar>
                      <Typography variant="body2" fontWeight={500}>{member.name || '—'}{isSelf && <Chip label={translate('shell.you')} size="small" sx={{ ml: 1 }} />}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2">{member.email}</Typography></TableCell>
                  <TableCell>
                    {isOwner && !isSelf && editingId === member.id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField select size="small" value={pendingRole} onChange={(e) => setPendingRole(e.target.value)} sx={{ width: 120 }}>
                          {roleOptions().map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
                        </TextField>
                        <Button size="small" variant="contained" onClick={() => handleSaveRole(member)} disabled={updateRole.isPending}>{translate('shell.save')}</Button>
                        <Button size="small" onClick={() => setEditingId(null)}>{translate('shell.cancel')}</Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Chip label={translate(`shell.${member.role}`, { defaultValue: member.role })} size="small" color={ROLE_COLORS[member.role] || 'default'} sx={{ textTransform: 'capitalize' }} />
                        {isOwner && !isSelf && member.role !== 'owner' && (
                          <Tooltip title={translate('shell.changeRole')}><IconButton size="small" onClick={() => startRoleEdit(member)}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell><Chip label={member.is_active ? translate('shell.active') : translate('shell.inactive')} size="small" color={member.is_active ? 'success' : 'default'} /></TableCell>
                  <TableCell><Typography variant="caption" color="text.secondary">{formatDate(member.joined_at)}</Typography></TableCell>
                  {isOwner && (
                    <TableCell align="right">
                      {!isSelf && member.role !== 'owner' && member.is_active && (
                        <Tooltip title={translate('shell.removeMember')}>
                          <IconButton size="small" color="error" disabled={removingId === member.id} onClick={() => { setRemoveError(null); setToRemove(member); }}>
                            {removingId === member.id ? <CircularProgress size={16} /> : <DeleteOutlineIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {isAdmin && <PendingInvitations />}

      <ConfirmActionDialog
        open={!!toRemove}
        title={translate('shell.removeMember')}
        confirmWord={translate('shell.removeConfirmWord')}
        confirmLabel={translate('shell.removeMember')}
        confirmColor="error"
        loading={!!toRemove && removingId === toRemove.id}
        error={removeError}
        onConfirm={confirmRemove}
        onClose={() => setToRemove(null)}
      >
        <Typography variant="body2">{translate('shell.removeMemberBody', { name: toRemove?.name || toRemove?.email })}
        </Typography>
      </ConfirmActionDialog>
    </Box>
  );
}
